import React, { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutGrid, GanttChart, TextAlignJustify, List } from 'lucide-react';
import { TabsList, TabsTrigger } from './ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { cn } from '@/lib/utils';

export type ViewSwitcherMode = 'grid' | 'table' | 'timeline' | 'list';

const ICONS: Record<ViewSwitcherMode, React.ComponentType<{ className?: string }>> = {
  grid: LayoutGrid,
  list: List,
  table: TextAlignJustify,
  timeline: GanttChart,
};

const DEFAULT_ARIA_LABELS: Record<ViewSwitcherMode, string> = {
  grid: 'Grid',
  list: 'List',
  table: 'Table',
  timeline: 'Timeline',
};

export interface ViewSwitcherProps {
  modes: readonly ViewSwitcherMode[];
  ariaLabelForMode?: (mode: ViewSwitcherMode) => string;
  listClassName?: string;
  triggerClassName?: string;
  showTooltips?: boolean;
}

export function useViewModeSearchParam<T extends ViewSwitcherMode>(options: {
  allowedModes: readonly T[];
  defaultMode: T;
  paramName?: string;
}): [T, (mode: T) => void] {
  const { allowedModes, defaultMode, paramName = 'view' } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const viewMode = useMemo((): T => {
    const raw = searchParams.get(paramName);
    if (raw && (allowedModes as readonly string[]).includes(raw)) {
      return raw as T;
    }
    return defaultMode;
  }, [searchParams, paramName, allowedModes, defaultMode]);

  const setViewMode = useCallback(
    (mode: T) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(paramName, mode);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams, paramName],
  );

  return [viewMode, setViewMode];
}

export function ViewSwitcher({
  modes,
  ariaLabelForMode,
  listClassName,
  triggerClassName,
  showTooltips = true,
}: ViewSwitcherProps) {
  const triggers = modes.map((mode) => {
    const Icon = ICONS[mode];
    const label = ariaLabelForMode?.(mode) ?? DEFAULT_ARIA_LABELS[mode];
    const trigger = (
      <TabsTrigger
        value={mode}
        aria-label={label}
        className={cn(
          'h-[30px] w-[30px] rounded-md p-0 text-muted-foreground aria-[selected=true]:bg-white aria-[selected=true]:text-[var(--scribane-green)] aria-[selected=true]:shadow-sm dark:aria-[selected=true]:bg-background dark:aria-[selected=true]:shadow-none',
          triggerClassName
        )}
      >
        <Icon className="h-4 w-4" />
      </TabsTrigger>
    );
    if (!showTooltips) {
      return <React.Fragment key={mode}>{trigger}</React.Fragment>;
    }
    return (
      <Tooltip key={mode}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
    );
  });

  const list = (
    <TabsList className={cn('h-[38px] gap-1 rounded-[8px] border border-border bg-muted p-1', listClassName)}>
      {triggers}
    </TabsList>
  );

  if (!showTooltips) {
    return list;
  }

  return <TooltipProvider delayDuration={0}>{list}</TooltipProvider>;
}
